/**
 * Utility functions for managing dynamic favicons based on brand and theme
 */

/**
 * Maps brand names from env/config to favicon directory names
 */
const brandToFaviconDir = {
  brytebridge: "brytebridge",
  "us-filing-services": "usfiling",
  "file-florida": "file-florida",
  "501-center": "501-center",
  "file-north-carolina": "file-north-carolina",
  "foundation-group": "brytebridge", // fallback to brytebridge if not found
};

/**
 * Gets the favicon directory name for a given brand
 */
export const getFaviconDir = (brandName) => {
  return brandToFaviconDir[brandName] || brandToFaviconDir.brytebridge;
};

/**
 * Updates favicon links in the document head based on brand and theme mode
 * @param {string} brandName - The brand name from VITE_BRAND
 * @param {string} mode - Theme mode: 'light' or 'dark'
 */
export const updateFavicons = (brandName, mode = "light") => {
  const faviconDir = getFaviconDir(brandName);
  const basePath = `/favicons/${faviconDir}`;

  // Check if brand has dark/light folders (most brands do)
  // file-florida only has a single favicon.png
  const hasThemeFolders = brandName !== "file-florida";

  if (!hasThemeFolders) {
    // For brands without theme folders, use the single favicon
    updateFaviconLink("icon", `${basePath}/favicon.png`, "image/png");
    return;
  }

  const themePath = `${basePath}/${mode}`;

  // Update standard favicon
  updateFaviconLink("icon", `${themePath}/favicon.ico`, "image/x-icon");
  updateFaviconLink("icon", `${themePath}/favicon-16x16.png`, "image/png", "16x16");
  updateFaviconLink("icon", `${themePath}/favicon-32x32.png`, "image/png", "32x32");

  // Update Apple touch icon
  updateFaviconLink("apple-touch-icon", `${themePath}/apple-touch-icon.png`, "image/png");

  // Update Android Chrome icons
  updateFaviconLink("icon", `${themePath}/android-chrome-192x192.png`, "image/png", "192x192");
  updateFaviconLink("icon", `${themePath}/android-chrome-512x512.png`, "image/png", "512x512");

  // Update web manifest
  updateFaviconLink("manifest", `${themePath}/site.webmanifest`, "application/manifest+json");
};

/**
 * Updates or creates a favicon link element in the document head
 * @param {string} rel - The rel attribute value
 * @param {string} href - The href path
 * @param {string} type - The type attribute value
 * @param {string} sizes - Optional sizes attribute value
 */
const updateFaviconLink = (rel, href, type, sizes = null) => {
  // Build selector based on whether sizes is provided
  let selector = `link[rel="${rel}"]`;
  if (sizes) {
    // When sizes is provided, match both rel and sizes
    selector = `link[rel="${rel}"][sizes="${sizes}"]`;
  } else {
    // When sizes is not provided, only match links without sizes attribute
    selector = `link[rel="${rel}"]:not([sizes])`;
  }

  let link = document.querySelector(selector);

  if (link) {
    // Update existing link
    link.href = href;
    if (type) link.type = type;
    if (sizes) link.sizes = sizes;
  } else {
    // Create new link element
    link = document.createElement("link");
    link.rel = rel;
    link.href = href;
    if (type) link.type = type;
    if (sizes) link.sizes = sizes;
    document.head.appendChild(link);
  }
};

/**
 * Initializes favicons on app load
 */
export const initializeFavicons = () => {
  const brandName = import.meta.env.VITE_BRAND || "brytebridge";
  const savedMode = localStorage.getItem("themeMode") || "light";
  updateFavicons(brandName, savedMode);
};

